/**
 * /dashboard/accounting/purchases/$id — Bill detail (G_ACC5).
 *
 * Routes to BillDetail which shows the MSME-overdue banner (if any),
 * the line items, and the state-machine actions (Edit / Receive /
 * Cancel) gated on the current status.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/purchases/[id]/page.tsx.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useBill } from "@/hooks/use-accounting";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { BillDetail } from "@/components/accounting/bill-detail";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/purchases/$id",
)({
  component: BillDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

function BillDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = useBill(numId, validId);

  return (
    <AccountingModuleShell
      title={q.data ? `Bill ${q.data.bill_no}` : "Bill"}
      actions={
        <Button asChild variant="outline" className="gap-1.5 lg:hidden">
          <Link to="/app/$tenantSlug/accounting/purchases" params={{ tenantSlug }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            Back to Purchases
          </Link>
        </Button>
      }
    >
      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid bill id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid bill id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this bill</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/accounting/purchases" params={{ tenantSlug }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to Bills
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && q.data && <BillDetail bill={q.data} />}
    </AccountingModuleShell>
  );
}
